import { Gender, genderTitles, type SubmitBuyerPassenger } from '@store/form';
import { type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';
import { BillsApiApiBillsPost } from '@clients/BillApi';
import { type ReserveType } from '@utils/itinerary';

export default abstract class BillHandler {
    isAutoReserve = false;

    get isSingleForRoundTrip() {
        return false;
    }

    /**
     * Registers the passengers info to the bill
     *
     * @async
     * @type {Promise<{result: boolean, departureTime: Date | null}>}
     * @memberof BillHandler
     */
    protected async registerPaxInfoToBill(params: {
        pax: SubmitBuyerPassenger[];
        email: string;
        mobile: string;
        destinationMobile: string;
        billId: number;
        customData: any;
        billCode: string;
        bypassPaxCountValidation?: boolean;
        entries: InvoiceEntry[];
        flightNumber?: string;
        origin?: string;
        message?: string;
    }): Promise<{
        result: boolean;
        departureTime: Date | null;
        data?: any;
    }> {
        await BillsApiApiBillsPost({
            addBillRequest: {
                Person: params.pax.map(p => ({
                    FirstName: p.firstName,
                    LastName: p.lastName,
                    PersianFirstName: p.persianFirstName,
                    PersianLastName: p.persianLastName,
                    Male: p.gender === Gender.Male,
                    NationalCode: p.nationalCode,
                    BirthDay: p.birthDate,
                    PassportExpiration: p.passportExpiry,
                    PassportNumber: p.passportNumber,
                    PaxType:
                        p.type === PaxType.Adult
                            ? 'ADL'
                            : p.type === PaxType.Child
                              ? 'CHD'
                              : 'INF',
                    Nationality: p.nationalityCode,
                })),
                Bill: {
                    Id: params.billId,
                    Email: params.email,
                    Mobile: params.mobile,
                    SecondaryMobile: params.mobile,
                },
                ByPassPaxCountValidation: !!params.bypassPaxCountValidation,
                Message: params.message,
            },
        });
        return {
            result: true,
            departureTime: null,
        };
    }

    /**
     * Registers the passengers info of the bill
     *
     * @async
     * @type {Promise<{result: boolean, departureTime: Date | null}>}
     * @memberof BillHandler
     */
    registerPaxInfo(params: {
        pax: SubmitBuyerPassenger[];
        email: string;
        mobile: string;
        destinationMobile: string;
        billId: number;
        customData: any;
        billCode: string;
        bypassPaxCountValidation?: boolean;
        entries: InvoiceEntry[];
        message?: string;
    }): Promise<{
        result: boolean;
        departureTime: Date | null;
        data?: any;
    }> {
        return this.registerPaxInfoToBill(params);
    }

    autoReserveRegisterPaxInfo(params: {
        pax: SubmitBuyerPassenger[];
        email: string;
        mobile: string;
        destinationMobile: string;
        billId: number;
        customData: any;
        billCode: string;
        bypassPaxCountValidation?: boolean;
        entries: InvoiceEntry[];
        flightNumber?: string;
        origin?: string;
        message?: string;
    }): Promise<{
        result: boolean;
        departureTime: Date | null;
        data?: any;
    }> {
        return this.registerPaxInfoToBill(params);
    }

    /**
     * Returns the title of passenger, according to the given gender and passenger type
     *
     * @type {string}
     * @memberof BillHandler
     */
    getTitle(type: PaxType, gender: Gender): string {
        switch (type) {
            case PaxType.Infant:
                return 'نوزاد';
            default:
                return genderTitles[gender] || '';
        }
    }

    /**
     * Performs the reservations that must be done just before payment
     *
     * @async
     * @type {Promise<void>}
     * @memberof BillHandler
     */
    abstract prepayReservation(
        billCode: string,
        params?: { price?: number }
    ): Promise<void>;

    concreteType?: ReserveType;
}
